import { HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, from, Observable, switchMap, throwError } from 'rxjs';
import { Constants } from '@app/core/constants';
import { RecaptchaService } from '@app/core/services/recaptcha.service';
import { ToastService } from '@app/shared/services/toast-service.service';

/**
 * Paths that must carry a reCAPTCHA token. Matched against the end of the request
 * URL so they hold for every environment, whose api root differs.
 * To protect another route, add its path here and nothing else.
 */
export const RECAPTCHA_PROTECTED_ROUTES: readonly string[] = [
  '/admin/accounts/login',
  '/admin/security/send-code-with-identifier',
];

/** Single action for every request, so route names are not reported to Google. */
const RECAPTCHA_ACTION = 'submit';
const TOKEN_ERROR_MESSAGE = 'Unable to verify your request. Please try again.';

export const recaptchaInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
  if (req.method !== 'POST' || !isProtected(req.url)) {
    return next(req);
  }

  const recaptcha = inject(RecaptchaService);
  const toast = inject(ToastService);

  return from(recaptcha.execute(RECAPTCHA_ACTION)).pipe(
    // Scoped to the token only, so failures from the request itself keep flowing
    // to the error interceptor untouched.
    catchError(error => {
      toast.error(TOKEN_ERROR_MESSAGE);
      return throwError(() => error);
    }),
    switchMap(token => next(withToken(req, token))),
  );
};

function isProtected(url: string): boolean {
  return RECAPTCHA_PROTECTED_ROUTES.some(route => url.endsWith(route));
}

/**
 * Merges the token into the request payload. `HttpService` wraps payloads as
 * `{ data: ... }`, so the token goes inside that when present and at the top
 * level otherwise. Bodies that cannot carry fields are left alone.
 */
function withToken(req: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
  const body = req.body;
  if (!body || typeof body !== 'object' || body instanceof FormData) {
    return req;
  }

  const payload = body as Record<string, unknown>;
  const data = payload['data'];
  const wrapped = data !== null && typeof data === 'object';

  return req.clone({
    body: wrapped
      ? {
          ...payload,
          data: { ...(data as Record<string, unknown>), [Constants.RECAPTCHA_FIELD]: token },
        }
      : { ...payload, [Constants.RECAPTCHA_FIELD]: token },
  });
}
